import DOMPurify from "dompurify"

type Json =
  | string
  | number
  | boolean
  | null
  | Json[]
  | { [key: string]: Json }

export type SanitizePayloadOptions = {
  multilineKeys?: string[]
  maxStringLength?: number
}

/* eslint-disable no-control-regex -- Intentional: sanitization requires matching control chars */
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g
const CONTROL_CHARS_KEEP_NEWLINE = /[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/g
/* eslint-enable no-control-regex */
const TAG_REGEX = /<\/?[^>]+>/g
const ZERO_WIDTH = /[\u200B-\u200D\uFEFF]/g

const stripZeroWidth = (value: string): string => value.replace(ZERO_WIDTH, "")

const stripHtmlTags = (value: string): string => value.replace(TAG_REGEX, "")

const tryNormalize = (value: string): string => {
  try {
    return value.normalize("NFC")
  } catch {
    return value
  }
}

export type SanitizeStringOptions = {
  maxLength?: number
  allowNewlines?: boolean
  collapseWhitespace?: boolean
  stripHtml?: boolean
  trim?: boolean
}

export const sanitizeString = (
  input: unknown,
  options: SanitizeStringOptions = {}
): string => {
  const {
    maxLength,
    allowNewlines = false,
    collapseWhitespace = true,
    stripHtml = true,
    trim = true,
  } = options

  if (input === null || input === undefined) {
    return ""
  }

  const controlPattern = allowNewlines
    ? CONTROL_CHARS_KEEP_NEWLINE
    : CONTROL_CHARS

  let value = tryNormalize(String(input))
  value = value.replace(controlPattern, "")
  value = stripZeroWidth(value)
  value = stripHtml ? stripHtmlTags(value) : value
  if (trim) {
    value = value.trim()
  }
  if (collapseWhitespace) {
    value = allowNewlines
      ? value
          .split(/\r?\n/)
          .map((line) => {
            const collapsed = line.replace(/[ \t\f\v\r]+/g, " ")
            return trim ? collapsed.trim() : collapsed
          })
          .filter((line) => line.length > 0)
          .join("\n")
      : value.replace(/\s+/g, " ")
  }
  if (maxLength && maxLength > 0) {
    value = value.slice(0, maxLength)
  }
  return value
}

export const sanitizeMultiline = (
  input: unknown,
  options: Omit<SanitizeStringOptions, "allowNewlines"> = {}
): string =>
  sanitizeString(input, {
    allowNewlines: true,
    ...options,
  })

export const sanitizeSlug = (
  input: unknown,
  options: { maxLength?: number } = {}
): string => {
  const base = sanitizeString(input, {
    allowNewlines: false,
    stripHtml: true,
    collapseWhitespace: true,
  })
  const lowered = base.toLowerCase()
  const slug = lowered
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
  return options.maxLength ? slug.slice(0, options.maxLength) : slug
}

export const sanitizeSearchTerm = (
  input: unknown,
  options: { maxLength?: number } = {}
): string =>
  sanitizeString(input, {
    allowNewlines: false,
    stripHtml: true,
    collapseWhitespace: true,
    maxLength: options.maxLength ?? 255,
  })

export const sanitizeInputValue = (
  value: string,
  options?: { trim?: boolean }
): string =>
  sanitizeString(value, {
    allowNewlines: false,
    maxLength: 512,
    stripHtml: true,
    trim: options?.trim ?? true,
  })

export const sanitizeTextareaValue = (
  value: string,
  options?: { trim?: boolean }
): string =>
  sanitizeMultiline(value, {
    maxLength: 4000,
    stripHtml: true,
    trim: options?.trim ?? true,
  })

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value)

const shouldTreatAsMultiline = (
  key: string,
  multilineKeys?: string[]
): boolean => {
  if (!multilineKeys || multilineKeys.length === 0) return false
  return multilineKeys.some(
    (candidate) => candidate.toLowerCase() === key.toLowerCase()
  )
}

const sanitizeValue = (
  key: string,
  value: Json,
  options: SanitizePayloadOptions
): Json => {
  if (typeof value === "string") {
    const targetMax = options.maxStringLength ?? 4000
    const allowNewlines = shouldTreatAsMultiline(key, options.multilineKeys)
    const sanitized = allowNewlines
      ? sanitizeTextareaValue(value).slice(0, targetMax)
      : sanitizeInputValue(value).slice(0, targetMax)
    return sanitized
  }
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeValue(key, entry as Json, options))
  }
  if (isPlainObject(value)) {
    return sanitizePayload(value, options)
  }
  return value
}

export const sanitizePayload = <T extends Record<string, Json>>(
  payload: T,
  options: SanitizePayloadOptions = {}
): T => {
  const sanitized: Record<string, Json> = {}
  for (const [key, value] of Object.entries(payload)) {
    sanitized[key] = sanitizeValue(key, value as Json, options)
  }
  return sanitized as T
}

export const sanitizeHtml = (html: string): string =>
  DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })

export const sanitizeStyleContent = (css: string): string => {
  const wrapped = `<style>${css}</style>`
  const sanitized = DOMPurify.sanitize(wrapped, {
    ALLOWED_TAGS: ["style"],
    ALLOWED_ATTR: ["type"],
  })
  const match = sanitized.match(/<style[^>]*>([\s\S]*)<\/style>/i)
  return match?.[1] ?? ""
}
